import React, { useMemo, useRef, useEffect, useLayoutEffect, useState } from "react";
import * as Mui from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import { constrainDateRangePopup } from "./dateRangePopupPosition";
import "bootstrap/dist/css/bootstrap.css";
import "../../assets/scss/datepicker.css";
import {
  designColors,
  designRadii,
  designShell,
} from "services/constants/designTokens";

const dateFormats = ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", moment.ISO_8601];

const asMoment = (date) => {
  const parsed = moment(date, dateFormats, true);
  return parsed.isValid() ? parsed : moment(date);
};

const buildRanges = ({ includeCurrentDate, showToday, thisMonthToDate }) => ({
  ...(showToday && {
    Today: [moment().toDate(), moment().toDate()],
  }),
  Yesterday: [
    moment().subtract(1, "days").toDate(),
    moment().subtract(1, "days").toDate(),
  ],
  "Last 7 Days": includeCurrentDate
    ? [moment().subtract(6, "days").toDate(), moment().toDate()]
    : [
        moment().subtract(7, "days").toDate(),
        moment().subtract(1, "days").toDate(),
      ],
  "Last 30 Days": includeCurrentDate
    ? [moment().subtract(29, "days").toDate(), moment().toDate()]
    : [
        moment().subtract(30, "days").toDate(),
        moment().subtract(1, "days").toDate(),
      ],
  "This Month": [
    moment().startOf("month").toDate(),
    // Approvals plan ahead, so the default runs to month end; reports opt in
    // to stop at today instead (1st -> current date).
    (thisMonthToDate ? moment() : moment().endOf("month")).toDate(),
  ],
  "Last Month": [
    moment().subtract(1, "month").startOf("month").toDate(),
    moment().subtract(1, "month").endOf("month").toDate(),
  ],
});

const formatRangeLabel = (start, end, numericDates) => {
  if (numericDates) {
    return start.isSame(end, "day")
      ? start.format("DD-MM-YYYY")
      : `${start.format("DD-MM-YYYY")}   -   ${end.format("DD-MM-YYYY")}`;
  }
  return start.isSame(end, "day")
    ? start.format("DD-MM-YYYY")
    : `${start.format("DD-MM-YYYY")} - ${end.format("DD-MM-YYYY")}`;
};

// Look for pickers sitting inside a FilterSection row, next to the labelled
// Department/Shift dropdowns: same light fill, teal border and 40px height,
// stretched to the cell with the chevron pushed to the far right. Pair it with
// the `staticLabel` prop (e.g. "Date :").
export const filterBarPickerSx = {
  minHeight: 40,
  height: 40,
  width: "100%",
  justifyContent: "space-between",
  backgroundColor: "#f5f7fa",
  border: "1px solid #00D4AA",
  "&:hover": {
    backgroundColor: "#f5f7fa",
    border: "1px solid #00D4AA",
  },
};

export const ApprovalDateRangeFilter = ({
  startDate,
  endDate,
  dateRangeLabel = "Last 7 Days",
  displayLabel,
  onChange,
  includeCurrentDate = true,
  showToday = true,
  // Approval screens increasingly need forward-looking ranges (planned OT,
  // off-day duty, etc.), so keep future dates available unless a caller
  // explicitly opts out.
  allowFutureDate = true,
  // Opt-in: make the "This Month" preset end today rather than on the last
  // day of the month.
  thisMonthToDate = false,
  minDate,
  maxDate,
  ranges,
  alwaysShowCalendars = false,
  // Opt-in single-day mode for reports whose API only ever takes one
  // `date` (Daily Report) rather than a start/end pair — skips the quick
  // "Last 7 Days"/"This Month" preset list (meaningless for one day) and
  // drops the "<label> - " prefix from the button, since formatRangeLabel
  // already collapses to a single date when start and end are the same day.
  singleDatePicker = false,
  drops = "down",
  // Optional viewport boundary for pickers in panels below a fixed app bar.
  popupTopOffset,
  // Opt-in, form-field look: white bordered box spanning the full width,
  // dates pushed left and the chevron pushed to the far right, plain
  // numeric DD/MM/YYYY dates, and no "<label> - " prefix — e.g. Bulk
  // Reprocess's "Select Date Period" step, which reads as a real form field
  // rather than a report-toolbar filter chip. Defaults to false so every
  // existing toolbar-filter usage keeps its current compact pill look.
  formFieldStyle = false,
  // Optional muted prefix shown before the date, e.g. "Date :" (matches the
  // static labels on the neighbouring filter dropdowns).
  staticLabel,
  sx = {},
}) => {
  const start = asMoment(startDate);
  const end = asMoment(endDate);
  const lastEmittedRangeRef = useRef("");
  const pickerRef = useRef(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const pickerKey = `${start.format("YYYY-MM-DD")}-${end.format("YYYY-MM-DD")}-${dateRangeLabel}`;

  // Install before the first click, and again when a range change remounts
  // the underlying picker. This also bounds moves during calendar expansion.
  useLayoutEffect(() => {
    if (popupTopOffset === undefined) return;
    const picker = pickerRef.current?.$picker?.data?.("daterangepicker");
    if (picker) constrainDateRangePopup(picker, popupTopOffset);
  }, [pickerKey, popupTopOffset]);

  // The underlying jQuery plugin positions its popup as position:absolute,
  // computed once from the trigger's document offset when it opens, and
  // only ever recalculates that on window resize (see its own `move()`
  // call wired to the resize event) — never on scroll. When the trigger
  // sits inside a scrollable container (like this panel's body), scrolling
  // it leaves the popup visually stuck in its original spot instead of
  // following the trigger. Fix: call the same move() the library uses for
  // resize, on any scroll while the popup is open. `capture: true` is
  // required to observe scroll on a nested scrollable ancestor at all,
  // since plain scroll events don't bubble up to window/document.
  useEffect(() => {
    if (!popupOpen) return undefined;
    const reposition = () => {
      const picker = pickerRef.current?.$picker?.data?.("daterangepicker");
      if (!picker) return;
      // The popup lives in <body>, so it isn't clipped by the scrolling
      // container and would float over the top bar once its trigger scrolls
      // out of view. Close it instead when the trigger leaves any scrollable
      // ancestor's visible area.
      const trigger = picker.element?.[0];
      if (trigger?.getBoundingClientRect) {
        const t = trigger.getBoundingClientRect();
        let node = trigger.parentElement;
        while (node && node !== document.body) {
          const { overflowY } = window.getComputedStyle(node);
          if (
            /(auto|scroll)/.test(overflowY) &&
            node.scrollHeight > node.clientHeight
          ) {
            const c = node.getBoundingClientRect();
            if (t.bottom <= c.top || t.top >= c.bottom) {
              picker.hide?.();
              return;
            }
          }
          node = node.parentElement;
        }
      }
      picker.move?.();
    };
    document.addEventListener("scroll", reposition, true);
    return () => document.removeEventListener("scroll", reposition, true);
  }, [popupOpen]);

  const resolvedRanges = useMemo(() => {
    if (singleDatePicker) return ranges;
    return ranges || buildRanges({ includeCurrentDate, showToday, thisMonthToDate });
  }, [includeCurrentDate, ranges, showToday, singleDatePicker, thisMonthToDate]);

  const resolvedMaxDate = allowFutureDate
    ? undefined
    : maxDate ||
      (includeCurrentDate || showToday
        ? moment().toDate()
        : moment().subtract(1, "days").toDate());

  const emitRangeChange = (rangeStart, rangeEnd, rangeLabel) => {
    const nextLabel =
      typeof rangeLabel === "string" && rangeLabel.trim()
        ? rangeLabel
        : "Custom Range";
    const nextRangeKey = `${moment(rangeStart).format("YYYY-MM-DD")}|${moment(rangeEnd).format("YYYY-MM-DD")}|${nextLabel}`;

    if (lastEmittedRangeRef.current === nextRangeKey) {
      return;
    }

    lastEmittedRangeRef.current = nextRangeKey;
    onChange?.(rangeStart, rangeEnd, nextLabel);
  };

  const handleCallback = (rangeStart, rangeEnd, rangeLabel) => {
    emitRangeChange(rangeStart, rangeEnd, rangeLabel);
  };

  const handleApply = (_event, picker) => {
    emitRangeChange(picker.startDate, picker.endDate, picker.chosenLabel);
  };

  return (
    <DateRangePicker
      key={pickerKey}
      initialSettings={{
        startDate: start.toDate(),
        endDate: end.toDate(),
        ...(minDate ? { minDate } : {}),
        ...(resolvedMaxDate ? { maxDate: resolvedMaxDate } : {}),
        ...(alwaysShowCalendars ? { alwaysShowCalendars: true } : {}),
        ...(singleDatePicker
          ? { singleDatePicker: true }
          : { ranges: resolvedRanges }),
        drops,
        parentEl: "body",
        applyClass: "custom-apply-btn",
        cancelClass: "custom-cancel-btn",
        locale: { format: "DD-MM-YYYY" },
      }}
      onApply={handleApply}
      onCallback={handleCallback}
      onShow={(_event, picker) => {
        if (popupTopOffset !== undefined) constrainDateRangePopup(picker, popupTopOffset);
        setPopupOpen(true);
      }}
      onHide={() => setPopupOpen(false)}
      ref={pickerRef}
    >
      <Mui.ButtonBase
        aria-label={`Date range: ${dateRangeLabel || "Custom Range"}`}
        sx={{
          ...designShell.compactOutlineButton,
          px: 1.75,
          py: 1,
          borderRadius: designRadii.control,
          color: designColors.slate800,
          backgroundColor: formFieldStyle
            ? designColors.white
            : designColors.slate100,
          // ButtonBase renders as a bare <button>, which CssBaseline resets
          // to border: 0 — plain borderColor has nothing to color without an
          // actual border-style/width. The pill variant gets away with that
          // (its slate100 fill still reads as a control), so it keeps the
          // original color-only override; the form-field's white box needs
          // a real drawn border (the full shorthand, with its color inlined
          // rather than a separate borderColor line, since the shorthand
          // would otherwise reset color to currentColor and clobber it).
          ...(formFieldStyle
            ? { border: `1px solid ${designColors.borderInput}` }
            : { borderColor: designColors.slate200 }),
          ...(formFieldStyle ? { width: "100%" } : {}),
          // The nowrap label would otherwise push the whole page past a phone
          // viewport (the browser then scales the page down, leaving a blank
          // strip on the right). A percentage cap cannot help here because
          // callers often wrap this in a content-sized flex parent, so the
          // cap is viewport-based (main content margin 75px + page padding).
          maxWidth: { xs: "calc(100vw - 110px)", md: "100%" },
          minWidth: 0,
          justifyContent: formFieldStyle ? "space-between" : "flex-start",
          "&:hover": {
            backgroundColor: formFieldStyle
              ? designColors.white
              : designColors.slate100,
            borderColor: formFieldStyle
              ? designColors.borderInputHover
              : designColors.slate300,
          },
          ...sx,
        }}
      >
        <Mui.Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
          <CalendarMonthIcon sx={{ fontSize: 18, color: formFieldStyle ? designColors.slate500 : "inherit" }} />
          {staticLabel ? (
            <Mui.Typography
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: designColors.slate500,
                whiteSpace: "nowrap",
              }}
            >
              {staticLabel}
            </Mui.Typography>
          ) : null}
          <Mui.Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 600,
              color: "inherit",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayLabel ? displayLabel : <>{singleDatePicker || formFieldStyle ? null : (
              <>
                {dateRangeLabel}
                {" - "}
              </>
            )}
            {formatRangeLabel(start, end, formFieldStyle)}</>}
          </Mui.Typography>
        </Mui.Stack>
        <KeyboardArrowRightIcon
          sx={{
            fontSize: 18,
            transform: "rotate(90deg)",
            flexShrink: 0,
            color: designColors.slate700,
            // 1.1 matches the gap the icon/text pair used to get from being
            // inside the same spacing={1.1} Stack, before the chevron moved
            // out to sit at the far end of a space-between row for
            // formFieldStyle — keeps the other, non-form-field consumers'
            // spacing pixel-identical to before.
            ml: formFieldStyle ? 1 : 1.1,
          }}
        />
      </Mui.ButtonBase>
    </DateRangePicker>
  );
};

export default ApprovalDateRangeFilter;
